"use client";

import { cn } from "@/utils/cn";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PromotionalCardConfig } from "./types";
import { usePromotionalCards } from "./use-cards";

const ROTATION_INDEX_KEY = "settlin_promo_card_rotation_index";

/**
 * Get and increment the rotation index from sessionStorage
 */
function getNextRotationIndex(totalCards: number): number {
	if (typeof window === "undefined" || totalCards === 0) return 0;

	try {
		const stored = sessionStorage.getItem(ROTATION_INDEX_KEY);
		const currentIndex = stored ? parseInt(stored, 10) : 0;
		const nextIndex = (currentIndex + 1) % totalCards;
		sessionStorage.setItem(ROTATION_INDEX_KEY, String(nextIndex));
		return currentIndex;
	} catch {
		return 0;
	}
}

/**
 * Hook to get cards in round-robin rotation for lists.
 * Each call to getNextCard returns the next card in sequence.
 * Cards cycle through all eligible cards before repeating.
 */
export function useRotatingCards(pageType: "search" | "sup" | "project") {
	const { potentialCards } = usePromotionalCards(pageType);

	// Shuffle cards once per session for variety
	const [shuffledCards, setShuffledCards] = useState<PromotionalCardConfig[]>([]);

	useEffect(() => {
		if (potentialCards.length === 0) {
			setShuffledCards([]);
			return;
		}
		// Fisher-Yates shuffle with deterministic seed based on session
		const cards = [...potentialCards];
		for (let i = cards.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[cards[i], cards[j]] = [cards[j], cards[i]];
		}
		setShuffledCards(cards);
	}, [potentialCards]);

	// Track index within this component instance
	const localIndexRef = useRef(0);

	const getNextCard = useCallback((): PromotionalCardConfig | undefined => {
		if (shuffledCards.length === 0) return undefined;

		// Get the next index from session storage (global across page)
		const globalIndex = getNextRotationIndex(shuffledCards.length);
		// Combine with local index for multiple cards on same page
		const combinedIndex = (globalIndex + localIndexRef.current) % shuffledCards.length;
		localIndexRef.current++;

		return shuffledCards[combinedIndex];
	}, [shuffledCards]);

	return {
		getNextCard,
		totalCards: shuffledCards.length,
		cards: shuffledCards,
	};
}

/**
 * Hook to get a single random card for SUP/project pages.
 * Uses useMemo to ensure card is stable per page load.
 */
export function useRandomCard(pageType: "search" | "sup" | "project") {
	const { potentialCards, getRandomCards } = usePromotionalCards(pageType);
	const [clientCard, setClientCard] = useState<PromotionalCardConfig | undefined>(undefined);

	useEffect(() => {
		if (potentialCards.length > 0) {
			const selected = getRandomCards(1, potentialCards);
			setClientCard(selected[0]);
		}
	}, [potentialCards, getRandomCards]);

	return { card: clientCard };
}

/**
 * Hook to get a random position index for inserting promotional cards.
 * Returns a stable position per page load within the given range.
 */
export function useRandomPosition(maxPositions: number): number {
	const [position, setPosition] = useState(0);

	useEffect(() => {
		if (maxPositions > 0) {
			setPosition(Math.floor(Math.random() * maxPositions));
		}
	}, [maxPositions]);

	return useMemo(() => {
		if (maxPositions <= 0) return 0;
		return position;
	}, [maxPositions, position]);
}

interface PromotionalCardRendererProps {
	card: PromotionalCardConfig;
	className?: string;
}

/**
 * Component to render a promotional card from config
 */
export function PromotionalCardRenderer({ card, className }: PromotionalCardRendererProps) {
	const CardComponent = card.component;

	return <CardComponent className={className} />;
}

interface PromotionalCardsSectionProps {
	pageType: "sup" | "project";
	className?: string;
	/** Which slot this is (0-indexed). Card renders only if this matches the random position */
	slot?: number;
	/** Total number of slots available. Used to calculate random position */
	totalSlots?: number;
}

/**
 * A section that displays a single random promotional card for SUP and Project pages.
 * Uses section-matching padding (px-2 @4xl:px-0) by default.
 *
 * When slot and totalSlots are provided, the card only renders if the random position
 * matches this slot. This allows placing multiple PromotionalCardsSections throughout
 * the page with only one actually rendering.
 */
export function PromotionalCardsSection({ pageType, className, slot, totalSlots }: PromotionalCardsSectionProps) {
	const { card } = useRandomCard(pageType);
	const randomPosition = useRandomPosition(totalSlots ?? 1);

	// If slot system is being used, only render if this slot matches
	if (slot !== undefined && totalSlots !== undefined && slot !== randomPosition) {
		return null;
	}

	if (!card) {
		return null;
	}

	return (
		<div className={cn("px-4", className)}>
			<PromotionalCardRenderer card={card} />
		</div>
	);
}
